import { UserRole } from "constants/userRole";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetOrderQuery } from "store/queries/order";
import { userSelector } from "store/slices/user";

import {
  canUseQueryAccount,
  resolveStaffChatAccountSource,
} from "./staffChatWidget.helpers";

export interface IStaffChatAccountId {
  /** The account the widget chats as; undefined while resolving or when there is none. */
  accountId: string | undefined;
  /** True while the estimate the account comes from is still loading: render nothing. */
  isResolving: boolean;
}

/**
 * CLE-3043 — turns resolveStaffChatAccountSource into the account the staff widget chats as.
 *
 * On an estimate, copy or draft review page the account is the order's own `accountId`, read with the
 * same `useGetOrderQuery(id)` the page uses (RTK Query dedupes the two into one request). `currentData`,
 * not `data`, so moving from one estimate to another never reports the previous estimate's account.
 * While that order is loading the widget renders nothing — never another hospital's widget for a
 * moment — and if the request fails, or the order carries no account, it still renders nothing.
 *
 * `?accountId=` (on a page with no estimate) is used only when the user may use that account
 * (canUseQueryAccount); otherwise it is ignored and the widget chats as `currentAccount`.
 */
export function useStaffChatAccountId(): IStaffChatAccountId {
  const { pathname, search } = useLocation();
  const { currentAccount, scope, accountIds } = useSelector(userSelector);
  const source = resolveStaffChatAccountSource(pathname, search);
  const orderId = source.kind === "order" ? source.orderId : undefined;

  const { currentData, isError } = useGetOrderQuery(orderId, {
    skip: !orderId,
  });

  if (source.kind === "url") {
    return { accountId: source.accountId, isResolving: false };
  }
  if (
    source.kind === "query" &&
    canUseQueryAccount(
      source.accountId,
      scope === UserRole.SUPER_ADMIN,
      accountIds
    )
  ) {
    return { accountId: source.accountId, isResolving: false };
  }
  if (source.kind === "order") {
    if (isError) return { accountId: undefined, isResolving: false };
    if (!currentData) return { accountId: undefined, isResolving: true };
    return {
      accountId: currentData.data?.accountId || undefined,
      isResolving: false,
    };
  }
  return { accountId: currentAccount || undefined, isResolving: false };
}
